import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { cn } from '@/lib/tailwind-utils';
import { CalendarIcon, X } from 'lucide-react';
import { useState } from 'react';
import { DayPickerSingleProps, SelectSingleEventHandler } from 'react-day-picker';

interface TableFilterDatePickerProps {
  calendarProps?: Omit<DayPickerSingleProps, 'mode'>;
  placeholder?: string;
  closeOnSelect?: boolean;
  onClear?: () => void;
  'data-testid'?: string;
}

export const TableFilterDatePicker = ({
  calendarProps = {},
  placeholder,
  closeOnSelect = true,
  onClear,
  'data-testid': dataTestId,
}: TableFilterDatePickerProps) => {
  const { selected } = calendarProps;
  const [isCalendarOpen, setIsCalendarOpen] = useState(false);
  const { onSelect, ...othersCalendarProps } = calendarProps;

  const handleOnSelect: SelectSingleEventHandler = (...selectedArgs) => {
    onSelect?.(...selectedArgs);
    if (closeOnSelect) {
      setIsCalendarOpen(false);
    }
  };

  return (
    <>
      <Popover open={isCalendarOpen} onOpenChange={setIsCalendarOpen}>
        <PopoverTrigger asChild>
          <Button
            variant={'outline'}
            className={cn('w-[240px] pl-3 text-left font-normal relative', !selected && 'text-muted-foreground')}
            data-testid={dataTestId}
          >
            {selected ? selected.toLocaleString('en-US', { dateStyle: 'long' }) : <span>{placeholder}</span>}
            <div className="ml-auto flex items-center gap-1">
              {onClear && selected && (
                <X
                  className="h-4 w-4 opacity-70 hover:opacity-100"
                  onClick={(e) => {
                    onClear();
                    e.stopPropagation();
                  }}
                  role="button"
                  aria-label="Clear date selection"
                />
              )}
              <CalendarIcon className="h-4 w-4 opacity-50" />
            </div>
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="start">
          <Calendar mode="single" onSelect={handleOnSelect} initialFocus {...othersCalendarProps} />
        </PopoverContent>
      </Popover>
    </>
  );
};
